import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Plus, Users, FileText, Loader2,
  CheckCircle, XCircle, Clock, AlertTriangle, Settings,
  Building2, GraduationCap, Globe, Phone, Award, BookOpen, Link2, MessageSquare
} from 'lucide-react';

const SPECIALTIES = [
  "Cardiology", "Neurology", "Oncology", "Surgery", "Pediatrics",
  "Dermatology", "Psychiatry", "Orthopedics", "Radiology", "Pathology",
  "Emergency Medicine", "Internal Medicine", "Public Health", "Pharmacology",
  "Genetics", "Immunology", "Infectious Disease", "Biomedical Engineering"
];

// ====== PROFILE FORM COMPONENT ======
const ProfileForm = ({ pf, setPf }) => (
  <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
    {/* Personal Information */}
    <div>
      <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
        <Users className="h-4 w-4 text-[#1a5c5c]" /> Personal Information
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-600">Full Name *</Label><Input value={pf.name} onChange={e => setPf({ ...pf, name: e.target.value })} data-testid="mentor-name" /></div>
        <div><Label className="text-xs text-slate-600">Phone</Label>
          <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" /><Input value={pf.phone} onChange={e => setPf({ ...pf, phone: e.target.value })} className="pl-9" placeholder="+91..." data-testid="mentor-phone" /></div>
        </div>
        <div><Label className="text-xs text-slate-600">City / Location</Label><Input value={pf.city} onChange={e => setPf({ ...pf, city: e.target.value })} placeholder="e.g. New Delhi" data-testid="mentor-city" /></div>
        <div>
          <Label className="text-xs text-slate-600">Preferred Communication</Label>
          <span className="md:col-span-1">
            <Select value={pf.preferred_communication} onValueChange={v => setPf({ ...pf, preferred_communication: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="video_call">Video Call</SelectItem>
              </SelectContent>
            </Select>
          </span>
        </div>
        <div className="md:col-span-2"><Label className="text-xs text-slate-600">Languages Spoken (comma separated)</Label><Input value={pf.languages} onChange={e => setPf({ ...pf, languages: e.target.value })} placeholder="English, Hindi, Tamil" data-testid="mentor-languages" /></div>
      </div>
    </div>

    <Separator />

    {/* Institution & Academic */}
    <div>
      <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
        <Building2 className="h-4 w-4 text-[#1a5c5c]" /> Institution & Academics
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-600">Institution / Hospital *</Label><Input value={pf.institution} onChange={e => setPf({ ...pf, institution: e.target.value })} placeholder="e.g. AIIMS Delhi" data-testid="mentor-institution" /></div>
        <div><Label className="text-xs text-slate-600">Department</Label><Input value={pf.department} onChange={e => setPf({ ...pf, department: e.target.value })} placeholder="e.g. Department of Cardiology" data-testid="mentor-department" /></div>
        <div>
          <Label className="text-xs text-slate-600">Primary Specialization *</Label>
          <Select value={pf.specialization} onValueChange={v => setPf({ ...pf, specialization: v })}>
            <SelectTrigger data-testid="mentor-specialization"><SelectValue placeholder="Select specialty" /></SelectTrigger>
            <SelectContent>{SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Secondary Specialization</Label>
          <Select value={pf.secondary_specialization} onValueChange={v => setPf({ ...pf, secondary_specialization: v })}>
            <SelectTrigger data-testid="mentor-secondary-spec"><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs text-slate-600">Qualifications / Degrees (comma separated)</Label><Input value={pf.qualifications} onChange={e => setPf({ ...pf, qualifications: e.target.value })} placeholder="MBBS, MD, DM, PhD" data-testid="mentor-qualifications" /></div>
        <div><Label className="text-xs text-slate-600">Experience Description</Label><Input value={pf.experience} onChange={e => setPf({ ...pf, experience: e.target.value })} placeholder="e.g. Senior Consultant" data-testid="mentor-experience" /></div>
        <div><Label className="text-xs text-slate-600">Years of Experience</Label><Input type="number" min="0" value={pf.years_of_experience} onChange={e => setPf({ ...pf, years_of_experience: e.target.value })} data-testid="mentor-years" /></div>
      </div>
    </div>

    <Separator />

    {/* Research & Publications */}
    <div>
      <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
        <BookOpen className="h-4 w-4 text-[#1a5c5c]" /> Research & Publications
      </h4>
      <div className="space-y-3">
        <div><Label className="text-xs text-slate-600">Research Areas / Interests (comma separated)</Label><Input value={pf.research_areas} onChange={e => setPf({ ...pf, research_areas: e.target.value })} placeholder="e.g. AI in Medicine, Clinical Trials, Genomics" data-testid="mentor-research-areas" /></div>
        <div><Label className="text-xs text-slate-600">Publications (one per line)</Label><Textarea value={pf.publications} onChange={e => setPf({ ...pf, publications: e.target.value })} rows={4} placeholder={"Nature Medicine 2024 - Title of paper\nThe Lancet 2023 - Another publication\nNew England Journal of Medicine 2022 - Paper title"} data-testid="mentor-publications" /></div>
        <div><Label className="text-xs text-slate-600">Awards & Honors (one per line)</Label><Textarea value={pf.awards} onChange={e => setPf({ ...pf, awards: e.target.value })} rows={2} placeholder={"Best Researcher Award 2023\nFellowship in Cardiology 2020"} data-testid="mentor-awards" /></div>
        <div><Label className="text-xs text-slate-600">Teaching Experience</Label><Input value={pf.teaching_experience} onChange={e => setPf({ ...pf, teaching_experience: e.target.value })} placeholder="e.g. Professor for 5 years, Guest Lecturer" data-testid="mentor-teaching" /></div>
        <div><Label className="text-xs text-slate-600">Mentorship Capacity (max students at once)</Label><Input type="number" min="1" value={pf.mentorship_capacity} onChange={e => setPf({ ...pf, mentorship_capacity: e.target.value })} placeholder="e.g. 5" data-testid="mentor-capacity" /></div>
      </div>
    </div>

    <Separator />

    {/* Online Presence */}
    <div>
      <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
        <Globe className="h-4 w-4 text-[#1a5c5c]" /> Online Presence
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-600">LinkedIn Profile URL</Label>
          <div className="relative"><Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" /><Input value={pf.linkedin_url} onChange={e => setPf({ ...pf, linkedin_url: e.target.value })} className="pl-9" placeholder="https://linkedin.com/in/..." data-testid="mentor-linkedin" /></div>
        </div>
        <div><Label className="text-xs text-slate-600">Personal/Academic Website</Label>
          <div className="relative"><Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" /><Input value={pf.website_url} onChange={e => setPf({ ...pf, website_url: e.target.value })} className="pl-9" placeholder="https://..." data-testid="mentor-website" /></div>
        </div>
        <div className="md:col-span-2"><Label className="text-xs text-slate-600">ORCID ID</Label><Input value={pf.orcid_id} onChange={e => setPf({ ...pf, orcid_id: e.target.value })} placeholder="0000-0002-1234-5678" data-testid="mentor-orcid" /></div>
      </div>
    </div>

    <Separator />

    {/* Bio */}
    <div>
      <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
        <GraduationCap className="h-4 w-4 text-[#1a5c5c]" /> About You
      </h4>
      <Textarea value={pf.bio} onChange={e => setPf({ ...pf, bio: e.target.value })} rows={4}
        placeholder="Write a detailed bio about your research experience, teaching philosophy, and what you can offer as a mentor. This helps students decide if you're the right mentor for them."
        data-testid="mentor-bio" />
      <p className="text-xs text-slate-400 mt-1">This will be visible to students browsing your projects.</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MentorDashboard() {
  const { user, api } = useAuth();
  const [tab, setTab] = useState('overview');
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [createRoomDialog, setCreateRoomDialog] = useState(false);
  const [profileDialog, setProfileDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '', description: '', specialty: '', duration: '',
    mode: 'online', paid: false, price: 0, slots_available: 1, requirements: ''
  });
  const [newRoom, setNewRoom] = useState({
    title: '', description: '', project_id: ''
  });
  const [pf, setPf] = useState({
    name: '', institution: '', department: '', specialization: '',
    secondary_specialization: '', experience: '', years_of_experience: '',
    bio: '', qualifications: '', publications: '', research_areas: '',
    linkedin_url: '', website_url: '', orcid_id: '', teaching_experience: '',
    mentorship_capacity: '', preferred_communication: 'email', languages: '',
    awards: '', phone: '', city: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('prasha_token');
      // Fetch projects and applications in parallel; rooms separately so a failure
      // there doesn't prevent the main dashboard from loading.
      const [projRes, appRes] = await Promise.all([
        api.get('/mentor/projects'),
        api.get('/applications'),
      ]);
      setProjects(projRes.data.projects || []);
      setApplications(appRes.data.applications || []);

      // Research rooms use a raw fetch — handle failure gracefully
      try {
        const roomsRes = await fetch(`${BACKEND_URL}/api/research-rooms`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json());
        setRooms(roomsRes.rooms || []);
      } catch (roomErr) {
        console.warn('Failed to load research rooms:', roomErr);
        setRooms([]);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user) {
      const p = user.profile || {};
      setPf({
        name: user.name || '',
        institution: p.institution || '', department: p.department || '',
        specialization: p.specialization || '', secondary_specialization: p.secondary_specialization || '',
        experience: p.experience || '', years_of_experience: p.years_of_experience || '',
        bio: p.bio || '',
        qualifications: (p.qualifications || []).join(', '),
        publications: (p.publications || []).join('\n'),
        research_areas: (p.research_areas || []).join(', '),
        linkedin_url: p.linkedin_url || '', website_url: p.website_url || '',
        orcid_id: p.orcid_id || '', teaching_experience: p.teaching_experience || '',
        mentorship_capacity: p.mentorship_capacity || '',
        preferred_communication: p.preferred_communication || 'email',
        languages: (p.languages || []).join(', '),
        awards: (p.awards || []).join('\n'),
        phone: p.phone || '', city: p.city || ''
      });
    }
  }, [user]);

  const isProfileIncomplete = () => {
    return !pf.institution || !pf.specialization || !pf.bio;
  };

  const createRoom = async () => {
    if (!newRoom.title) {
      toast.error('Please enter a room title');
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem('prasha_token');
      const response = await fetch(`${BACKEND_URL}/api/research-rooms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newRoom)
      });
      if (!response.ok) throw new Error('Failed to create room');
      toast.success('Research room created successfully!');
      setCreateRoomDialog(false);
      setNewRoom({ title: '', description: '', project_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  const createProject = async () => {
    if (!newProject.title || !newProject.description || !newProject.specialty || !newProject.duration) {
      toast.error('Please fill all required fields'); return;
    }
    setCreating(true);
    try {
      await api.post('/projects', { ...newProject, price: newProject.paid ? parseFloat(newProject.price) : 0 });
      toast.success('Project submitted for review!');
      setCreateDialog(false);
      setNewProject({ title: '', description: '', specialty: '', duration: '', mode: 'online', paid: false, price: 0, slots_available: 1, requirements: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally { setCreating(false); }
  };

  const updateAppStatus = async (applicationId, decision) => {
    try {
      await api.put(`/applications/${applicationId}/mentor-decision`, { decision });
      toast.success(`Application ${decision}ed`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const saveProfile = async () => {
    if (!pf.institution || !pf.specialization) {
      toast.error('Institution and Specialization are required'); return;
    }
    setSaving(true);
    try {
      await api.put('/users/profile', {
        name: pf.name,
        institution: pf.institution, department: pf.department,
        specialization: pf.specialization, secondary_specialization: pf.secondary_specialization,
        experience: pf.experience,
        years_of_experience: pf.years_of_experience ? parseInt(pf.years_of_experience) : null,
        bio: pf.bio,
        qualifications: pf.qualifications.split(',').map(s => s.trim()).filter(Boolean),
        publications: pf.publications.split('\n').map(s => s.trim()).filter(Boolean),
        research_areas: pf.research_areas.split(',').map(s => s.trim()).filter(Boolean),
        linkedin_url: pf.linkedin_url, website_url: pf.website_url, orcid_id: pf.orcid_id,
        teaching_experience: pf.teaching_experience,
        mentorship_capacity: pf.mentorship_capacity ? parseInt(pf.mentorship_capacity) : null,
        preferred_communication: pf.preferred_communication,
        languages: pf.languages.split(',').map(s => s.trim()).filter(Boolean),
        awards: pf.awards.split('\n').map(s => s.trim()).filter(Boolean),
        phone: pf.phone, city: pf.city,
      });
      toast.success('Profile updated successfully!');
      setProfileDialog(false);
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const statusColor = (s) => {
    if (s === 'approved' || s === 'accepted' || s === 'enrolled' || s === 'accepted_by_mentor') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (s === 'rejected' || s === 'rejected_by_mentor') return 'bg-red-50 text-red-700 border-red-100';
    if (s === 'verified') return 'bg-blue-50 text-blue-700 border-blue-100';
    if (s === 'payment_pending') return 'bg-purple-50 text-purple-700 border-purple-100';
    return 'bg-amber-50 text-amber-700 border-amber-100';
  };

  const formatAppStatus = (s) => {
    const map = {
      'verified': 'Ready for Review',
      'accepted_by_mentor': 'Accepted',
      'rejected_by_mentor': 'Declined',
      'payment_pending': 'Pending Payment',
      'enrolled': 'Enrolled'
    };
    return map[s] || s?.replace(/_/g, ' ');
  };

  const stats = [
    { label: 'Projects', value: projects.length, icon: <FileText className="h-5 w-5" /> },
    { label: 'Approved', value: projects.filter(p => p.status === 'approved').length, icon: <CheckCircle className="h-5 w-5" /> },
    { label: 'Applicants', value: applications.length, icon: <Users className="h-5 w-5" /> },
    { label: 'To Review', value: applications.filter(a => a.verification_status === 'verified' && !a.mentor_status).length, icon: <Clock className="h-5 w-5" /> },
  ];



  // ====== PENDING VERIFICATION ======
  if (!user?.verified) {
    return (
      <div className="page-container min-h-screen bg-slate-50" data-testid="mentor-pending">
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-12">
          <div className="text-center mb-8">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Verification Pending</h2>
            <p className="text-slate-600">Your mentor account is pending admin verification. Complete your profile to speed up approval.</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Complete Your Mentor Profile</h3>
            {isProfileIncomplete() && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4 text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4 inline mr-1.5" />
                Complete at least your Institution, Specialization, and Bio for faster verification.
              </div>
            )}
            <ProfileForm pf={pf} setPf={setPf} />
            <div className="mt-6 flex justify-end">
              <Button onClick={saveProfile} disabled={saving} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="save-mentor-profile-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save Profile
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ====== MAIN DASHBOARD ======
  return (
    <div className="page-container min-h-screen bg-slate-50" data-testid="mentor-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Welcome, {user?.name}</h1>
            <p className="text-sm text-slate-500 mt-1">Mentor Dashboard{user?.profile?.institution ? ` - ${user.profile.institution}` : ''}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setProfileDialog(true)} className="text-slate-600" data-testid="mentor-edit-profile-btn">
              <Settings className="h-4 w-4 mr-2" /> Edit Profile
            </Button>
            <Button onClick={() => setCreateDialog(true)} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="create-project-btn">
              <Plus className="h-4 w-4 mr-2" /> Post Project
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="stat-card rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#1a5c5c]/10 flex items-center justify-center text-[#1a5c5c]">{s.icon}</div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="mentor-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="projects" data-testid="mentor-tab-projects">My Projects</TabsTrigger>
            <TabsTrigger value="applicants" data-testid="mentor-tab-applicants">
              Applicants
              {applications.filter(a => a.verification_status === 'verified' && !a.mentor_status).length > 0 && (
                <span className="ml-1.5 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] inline-flex items-center justify-center">
                  {applications.filter(a => a.verification_status === 'verified' && !a.mentor_status).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="research-rooms" data-testid="mentor-tab-research-rooms" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> Research Rooms
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-100 p-6">
                <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Recent Projects</h3>
                {projects.length === 0 ? <p className="text-sm text-slate-500 py-2">No projects yet. Click "Post Project" to create one.</p> : (
                  projects.slice(0, 5).map((p) => (
                    <div key={p.project_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                      <div><p className="text-sm font-medium text-slate-900">{p.title}</p><p className="text-xs text-slate-500">{p.specialty} - {p.duration}</p></div>
                      <Badge className={`${statusColor(p.status)} border text-xs capitalize`}>{p.status}</Badge>
                    </div>
                  ))
                )}
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-6">
                <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Recent Applicants</h3>
                {applications.length === 0 ? <p className="text-sm text-slate-500 py-2">No applicants yet.</p> : (
                  applications.slice(0, 5).map((a) => (
                    <div key={a.application_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{a.student_name}</p>
                        <p className="text-xs text-slate-500">{a.project_title}</p>
                      </div>
                      <Badge className={`${statusColor(a.status)} border text-xs capitalize`}>{a.status}</Badge>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="projects">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>All Projects</h3>
              {projects.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No projects yet. Click "Post Project" to create one.</p>
              ) : (
                <div className="space-y-4">
                  {projects.map((p) => (
                    <div key={p.project_id} className="border border-slate-100 rounded-lg p-4" data-testid={`mentor-project-${p.project_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{p.title}</h4>
                          <p className="text-xs text-slate-500 mt-1">{p.specialty} - {p.duration} - {p.mode}</p>
                          <p className="text-xs text-slate-400 mt-1">Slots: {p.slots_filled || 0}/{p.slots_available} {p.paid && <span className="ml-2 text-amber-600">${p.price}</span>}</p>
                        </div>
                        <Badge className={`${statusColor(p.status)} border text-xs capitalize shrink-0`}>{p.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="applicants">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>All Applicants</h3>
              {applications.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No applicants yet.</p>
              ) : (
                <div className="space-y-4">
                  {applications.map((a) => (
                    <div key={a.application_id} className="border border-slate-100 rounded-lg p-4" data-testid={`applicant-${a.application_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-[#1a5c5c] flex items-center justify-center text-white text-xs font-semibold">
                              {a.student_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-slate-900">{a.student_name}</h4>
                              <p className="text-xs text-slate-500">{a.student_email}</p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-2">Project: <span className="text-slate-600 font-medium">{a.project_title}</span></p>
                          {a.motivation_letter && (
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                              <p className="text-xs text-slate-500 font-medium mb-1">Motivation Letter:</p>
                              <p className="text-xs text-slate-600 whitespace-pre-wrap line-clamp-4">{a.motivation_letter}</p>
                            </div>
                          )}
                          <p className="text-xs text-slate-400 mt-2">Applied: {new Date(a.created_at).toLocaleString()}</p>
                          {a.verification_status === 'verified' && (
                            <Badge className="mt-2 bg-blue-50 text-blue-700 border-blue-100 text-xs">Verified by PRASHA CoRe</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {a.verification_status === 'verified' && !a.mentor_status ? (
                            <>
                              <Button size="sm" onClick={() => updateAppStatus(a.application_id, 'accept')} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid={`accept-${a.application_id}`}>
                                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Accept
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => updateAppStatus(a.application_id, 'reject')} className="text-red-600 border-red-200 h-8 text-xs" data-testid={`reject-${a.application_id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
                              </Button>
                            </>
                          ) : (
                            <Badge className={`${statusColor(a.status)} border text-xs`}>{formatAppStatus(a.status)}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="research-rooms">
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>Research Project Rooms</h3>
                <Button onClick={() => setCreateRoomDialog(true)} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white">
                  <Plus className="h-4 w-4 mr-2" /> Create Room
                </Button>
              </div>

              {rooms.length === 0 ? (
                <div className="text-center py-12 border border-slate-200 rounded-lg">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 mb-4">No research rooms yet</p>
                  <Button onClick={() => setCreateRoomDialog(true)} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white">
                    <Plus className="h-4 w-4 mr-2" /> Create Your First Room
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {rooms.map((room) => (
                    <div key={room.room_id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition">
                      <h4 className="font-semibold text-slate-900 mb-2">{room.title}</h4>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{room.description}</p>
                      <div className="flex items-center gap-4 mb-4 text-xs text-slate-600">
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" /> {room.participants?.length || 0} members
                        </span>
                        <span className={`px-2 py-1 rounded text-white text-xs font-semibold ${room.status === 'active' ? 'bg-green-500' : 'bg-slate-500'}`}>
                          {room.status}
                        </span>
                      </div>
                      <a href={`/research-room/${room.room_id}`} className="text-[#1a5c5c] hover:text-[#0f3535] text-sm font-medium">
                        Enter Room →
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Research Room Dialog */}
      <Dialog open={createRoomDialog} onOpenChange={setCreateRoomDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Create Research Room</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Room Title *</Label>
              <Input
                value={newRoom.title}
                onChange={e => setNewRoom({ ...newRoom, title: e.target.value })}
                placeholder="e.g. Cancer Immunotherapy Research"
                data-testid="room-title-input"
              />
            </div>
            <div>
              <Label className="text-sm">Description</Label>
              <Textarea
                value={newRoom.description}
                onChange={e => setNewRoom({ ...newRoom, description: e.target.value })}
                rows={3}
                placeholder="What is this research room about?"
                data-testid="room-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateRoomDialog(false)}>Cancel</Button>
            <Button onClick={createRoom} disabled={creating} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="create-room-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Create Room
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Post Research Project</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div><Label className="text-sm">Title *</Label><Input value={newProject.title} onChange={e => setNewProject({ ...newProject, title: e.target.value })} data-testid="project-title-input" /></div>
            <div><Label className="text-sm">Description *</Label><Textarea value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} rows={3} data-testid="project-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Specialty *</Label>
                <Select value={newProject.specialty} onValueChange={v => setNewProject({ ...newProject, specialty: v })}>
                  <SelectTrigger data-testid="project-specialty-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Duration *</Label><Input value={newProject.duration} onChange={e => setNewProject({ ...newProject, duration: e.target.value })} placeholder="e.g. 6 months" data-testid="project-duration-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Mode</Label>
                <Select value={newProject.mode} onValueChange={v => setNewProject({ ...newProject, mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="online">Online</SelectItem><SelectItem value="offline">Offline</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Slots Available</Label><Input type="number" min="1" value={newProject.slots_available} onChange={e => setNewProject({ ...newProject, slots_available: parseInt(e.target.value) || 1 })} /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={newProject.paid} onCheckedChange={v => setNewProject({ ...newProject, paid: v })} data-testid="project-paid-switch" />
              <Label className="text-sm">Paid Project</Label>
            </div>
            {newProject.paid && (
              <div><Label className="text-sm">Price (USD)</Label><Input type="number" min="0" step="0.01" value={newProject.price} onChange={e => setNewProject({ ...newProject, price: e.target.value })} data-testid="project-price-input" /></div>
            )}
            <div><Label className="text-sm">Requirements</Label><Textarea value={newProject.requirements} onChange={e => setNewProject({ ...newProject, requirements: e.target.value })} rows={2} placeholder="What skills/qualifications are needed?" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={createProject} disabled={creating} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="submit-project-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Submit Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detailed Profile Dialog */}
      <Dialog open={profileDialog} onOpenChange={setProfileDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Edit Mentor Profile</DialogTitle></DialogHeader>
          <ProfileForm pf={pf} setPf={setPf} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialog(false)}>Cancel</Button>
            <Button onClick={saveProfile} disabled={saving} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="save-mentor-profile-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
